import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectAgents } from '@/redux/agentsSlice';
import { selectUnreadNotificationCount } from '@/redux/notificationsSlice';
import {
  buildDocumentTitle,
  resolvePageTitleSegments,
} from '@/utils/pageTitle';

/**
 * Keeps `document.title` in step with the route, so a tab's label says which
 * page it is holding — `Conversations - Sales agent - Dialup` — rather than
 * repeating the product tagline on every one, and leads with the unread
 * notification count when there is one.
 *
 * Called once from Layout, which owns every route, rather than from each page:
 * a single owner means no two pages can race each other for the title, and the
 * agent name and active tab both come from state Layout already subscribes to.
 */
export function usePageTitle(): void {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const agents = useSelector(selectAgents);
  const unreadCount = useSelector(selectUnreadNotificationCount);

  const title = useMemo(
    () =>
      buildDocumentTitle(
        resolvePageTitleSegments({
          pathname,
          search,
          agents,
          translate: (key, fallback) => t(key, fallback),
        }),
        unreadCount
      ),
    [pathname, search, agents, unreadCount, t]
  );

  useEffect(() => {
    document.title = title;
  }, [title]);
}

export default usePageTitle;
